import React, { useState } from 'react';

interface ActionCardProps {
  action: any;
  enabled: boolean;
  onToggle: (actionId: number) => void;
  onEdit?: () => void;
  isOwned?: boolean;
}

const ActionCard: React.FC<ActionCardProps> = ({ action, enabled, onToggle, onEdit, isOwned }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <>
      <div 
        className="rounded-lg shadow-md p-6 flex flex-col gap-2 hover:shadow-lg transition-shadow duration-300 cursor-pointer"
        style={{ 
          backgroundColor: 'var(--rhea-card-bg)',
          border: '1px solid var(--rhea-card-border)'
        }}
        onClick={() => setShowDetails(true)}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <div 
              className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center"
              style={{ backgroundColor: 'var(--rhea-bg-tertiary)' }}
            >
              {action.image_url ? (
                <img 
                  src={action.image_url} 
                  alt={action.name} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent) {
                      parent.style.backgroundColor = 'var(--rhea-bg-tertiary)';
                    }
                  }}
                />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  style={{ color: 'var(--rhea-text-secondary)' }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              )}
            </div>
            <h2 
              className="text-xl font-bold"
              style={{ color: 'var(--rhea-text-primary)' }}
            >
              {action.name || 'Unnamed Action'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {isOwned && onEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="p-1 hover:opacity-80"
                style={{ color: 'var(--rhea-text-secondary)' }}
                aria-label="Edit action"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggle(action.id);
              }}
              className="relative w-10 h-6 focus:outline-none transition-colors duration-200 rounded-full"
              style={{ 
                backgroundColor: enabled ? 'var(--rhea-accent-primary)' : 'var(--rhea-bg-tertiary)'
              }}
              aria-pressed={enabled}
              aria-label="Toggle action enabled"
            >
              <span
                className="absolute left-0 top-0 w-6 h-6 rounded-full shadow transform transition-transform duration-200"
                style={{ 
                  backgroundColor: 'var(--rhea-text-primary)',
                  transform: enabled ? 'translateX(16px)' : 'translateX(0)'
                }}
              />
            </button>
          </div>
        </div>
        <p 
          className="mb-1"
          style={{ color: 'var(--rhea-text-secondary)' }}
        >
          {action.description || 'No description provided.'}
        </p>
      </div>

      {showDetails && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={() => setShowDetails(false)}
        >
          <div 
            className="rounded-lg shadow-lg p-8 w-full max-w-2xl relative"
            style={{ 
              backgroundColor: 'var(--rhea-card-bg)',
              border: '1px solid var(--rhea-card-border)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              className="absolute top-2 right-2 text-2xl hover:opacity-80"
              style={{ color: 'var(--rhea-text-secondary)' }}
              onClick={() => setShowDetails(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="flex items-start gap-6 mb-4">
              {action.image_url && (
                <img 
                  src={action.image_url} 
                  alt={action.name} 
                  className="w-24 h-24 rounded-lg object-cover"
                />
              )}
              <div>
                <h2 
                  className="text-2xl font-bold"
                  style={{ color: 'var(--rhea-text-primary)' }}
                >
                  {action.name}
                </h2>
                <p 
                  className="mt-2"
                  style={{ color: 'var(--rhea-text-secondary)' }}
                >
                  {action.description || 'No description provided.'}
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <h3 
                  className="text-lg font-semibold mb-1"
                  style={{ color: 'var(--rhea-text-primary)' }}
                >
                  Instruction
                </h3>
                <p style={{ color: 'var(--rhea-text-secondary)' }}>
                  {action.instruction || 'No instruction provided.'}
                </p>
              </div>
              <div>
                <h3 
                  className="text-lg font-semibold mb-1"
                  style={{ color: 'var(--rhea-text-primary)' }}
                >
                  Execution Type
                </h3>
                <div 
                  className="p-4 rounded"
                  style={{ backgroundColor: 'var(--rhea-bg-tertiary)' }}
                >
                  <p 
                    className="mb-2"
                    style={{ color: 'var(--rhea-text-secondary)' }}
                  >
                    Type: {action.execution_type?.type || 'Not specified'}
                  </p>
                  <p 
                    className="mb-2"
                    style={{ color: 'var(--rhea-text-secondary)' }}
                  >
                    URL Pattern: {action.execution_type?.url || 'Not specified'}
                  </p>
                  <p style={{ color: 'var(--rhea-text-secondary)' }}>
                    Example: {action.execution_type?.example || 'Not specified'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActionCard; 